import type { RuleSet } from '@/game/rules';
import {
  buildingLevel,
  type Command,
  levelDef,
  type PlayerId,
  promoteCandidates,
  promoteCost,
  type SimState,
  trainCost,
  unitCost,
  unitRank,
} from '@/game/sim';
import { orderedResources, resourceIcon } from '@/game/view/resources';

/**
 * What a command spends from the player's stock, in whole units, judged on
 * `state` as it was sent (pure, no React): a unit's training, a site, a
 * building's next level, a promotion or a rank bought per unit. Null for
 * commands that spend nothing. The resources pill flashes it once the
 * command lands, so a click visibly took effect.
 */
export function commandSpend(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  command: Command
): Record<string, number> | null {
  const total: Record<string, number> = {};
  const add = (cost: Readonly<Record<string, number>> | undefined, n = 1) => {
    for (const [res, amount] of Object.entries(cost ?? {})) {
      total[res] = (total[res] ?? 0) + amount * n;
    }
  };
  switch (command.type) {
    case 'spawn':
      add(unitCost(state, rules, playerId, command.unitType));
      break;
    case 'place':
      add(rules.buildings[command.buildingType]?.cost);
      break;
    case 'upgrade': {
      const b = state.buildings[command.buildingId];
      const def = b && rules.buildings[b.type];
      if (b && def) add(levelDef(def, buildingLevel(b) + 1)?.cost);
      break;
    }
    case 'train': {
      const ids = 'unitIds' in command ? [...new Set(command.unitIds)] : [command.unitId];
      for (const id of ids) {
        const u = state.units[id];
        if (u?.owner !== playerId) continue;
        const rank = unitRank(rules, u);
        if (rank < rules.veterancy.ranks.length) add(trainCost(rules, rank + 1));
      }
      break;
    }
    case 'promote': {
      if ('unitIds' in command) {
        for (const id of new Set(command.unitIds)) {
          const u = state.units[id];
          if (u?.owner === playerId && u.type !== command.to) {
            add(promoteCost(rules, u.type, command.to));
          }
        }
        break;
      }
      // A city's best units: as many as it asks for, of those there now.
      const units = promoteCandidates(
        state,
        rules,
        playerId,
        command.cityId,
        command.to,
        command.from
      );
      const count = command.count ?? 1;
      for (const u of units.slice(0, count === 'all' ? units.length : count)) {
        add(promoteCost(rules, u.type, command.to));
      }
      break;
    }
    default:
      return null;
  }
  return Object.values(total).some((n) => n > 0) ? total : null;
}

/** The resource-wise sum of several spends. */
export function sumSpends(
  spends: readonly Readonly<Record<string, number>>[]
): Record<string, number> {
  const total: Record<string, number> = {};
  for (const s of spends)
    for (const [res, n] of Object.entries(s)) total[res] = (total[res] ?? 0) + n;
  return total;
}

/** "−20🌾 −12🪵": a spend as the pill flashes it (whole units, rounded up). */
export function spendLabel(amounts: Readonly<Record<string, number>>): string {
  return orderedResources(amounts)
    .filter((res) => (amounts[res] ?? 0) > 0)
    .map((res) => `−${Math.ceil(amounts[res] ?? 0)}${resourceIcon(res)}`)
    .join(' ');
}
